import { useSnackbar } from 'notistack';
import { ButtonGroup, TextField, Button, Stack } from '@mui/material';
import { Loader, Modal } from '../../../components';
import {
  getFieldWalkthroughDeficiencyReport,
  sendDeficiencyReport,
  generateDeficiencyReport
} from '../../../fetch';
import { FC, useState } from 'react';
import { downloadFile, isEmail } from '../../../helpers';
import { Close, Download, FileDownload, Send } from '@mui/icons-material';
import { IFieldWalkthroughDetail } from '../../../models';

interface IDeficiencyReportButton {
  isFieldWalkthroughLoading: boolean;
  walkthrough: IFieldWalkthroughDetail | null;
  fieldWalkthroughId: string;
}

export const DeficiencyReportButton: FC<IDeficiencyReportButton> = ({
  isFieldWalkthroughLoading,
  walkthrough,
  fieldWalkthroughId
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [showModal, setShowModal] = useState(false);
  const [mailToInput, setMailToInput] = useState<string>('');
  const [isGenerating, setGenerating] = useState(false);
  const [deficiencyReportName, setDeficiencyReportName] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);
  const fetchDeficiencyReport = async (id: string) => {
    try {
      setIsDownloading(true);
      const res = await getFieldWalkthroughDeficiencyReport(id);
      downloadFile(res, deficiencyReportName ?? 'Walkthrough Deficiency Report');
    } catch (error) {
      enqueueSnackbar(error?.Detail || `Error downloading deficiency report, please try again.`, {
        variant: 'error'
      });
    } finally {
      setIsDownloading(false);
    }
  };
  const handleGeneratingDeficiencyReport = async (id: string) => {
    try {
      setGenerating(true);
      const res = await generateDeficiencyReport(id);
      const url = res.data.replace(/\\/g, '/').replace(/\.[^/.]+$/, '');
      const fileName = url.substring(url.lastIndexOf('/') + 1);
      setDeficiencyReportName(fileName);
    } catch (error) {
      enqueueSnackbar(error?.Detail || `Error generating deficiency report, please try again.`, {
        variant: 'error'
      });
    } finally {
      setGenerating(false);
    }
  };
  return (
    <>
      <Button
        variant="contained"
        disabled={isFieldWalkthroughLoading || !walkthrough}
        startIcon={<FileDownload />}
        onClick={() => {
          setShowModal(!showModal);
          handleGeneratingDeficiencyReport(fieldWalkthroughId);
        }}
        sx={{
          width: {
            xs: '100%',
            sm: 'auto'
          },
          marginBottom: {
            xs: '1rem',
            sm: 0
          }
        }}
      >
        Generate Deficiency Report
      </Button>
      <Modal
        open={showModal}
        onClose={() => {
          setMailToInput('');
          setShowModal(false);
        }}
        maxWidth="xs"
        title="Deficiency Report"
      >
        {(isGenerating || isDownloading) && (
          <Loader
            type="overlay"
            position="centered"
            title={isGenerating ? 'Generating report...' : 'Downloading report...'}
          />
        )}
        <Stack gap={1} mt={1}>
          <Button
            type="button"
            onClick={() => {
              fetchDeficiencyReport(fieldWalkthroughId);
            }}
            color="primary"
            startIcon={<Download />}
            disabled={isGenerating || isDownloading}
            variant="contained"
            fullWidth
          >
            Download Report
          </Button>
          <TextField
            fullWidth
            variant="outlined"
            autoComplete="nope"
            label="Mail To"
            name="mailTo"
            value={mailToInput}
            error={mailToInput && !isEmail(mailToInput) ? true : false}
            helperText={mailToInput && !isEmail(mailToInput) ? 'Invalid email' : undefined}
            size="small"
            onChange={e => {
              setMailToInput(e.target.value);
            }}
          />

          <ButtonGroup>
            <Button
              disabled={!mailToInput || isGenerating || !isEmail(mailToInput)}
              type="button"
              onClick={async () => {
                try {
                  setGenerating(true);
                  await sendDeficiencyReport(fieldWalkthroughId, mailToInput);
                  setMailToInput('');
                  setShowModal(false);
                } catch (error) {
                  enqueueSnackbar(error?.Detail ?? `Error sending deficiency report.`, {
                    variant: 'error'
                  });
                } finally {
                  setGenerating(false);
                }
              }}
              variant="contained"
              color="primary"
              startIcon={<Send />}
            >
              Send Report Email
            </Button>
            <Button
              type="button"
              variant="contained"
              color="inherit"
              startIcon={<Close />}
              onClick={() => {
                setShowModal(false);
                setMailToInput('');
              }}
            >
              Cancel
            </Button>
          </ButtonGroup>
        </Stack>
      </Modal>
    </>
  );
};
